import {createSlice} from '@reduxjs/toolkit'
import {ISavedSearchFE} from 'interfaces/ISavedSearchFE'
import {
  classifyTextWRecentSearch,
  deleteSavedRecentSearches,
  fetchSavedRecentSearches,
  fetchSavedSearch,
  updateSavedSearch,
} from 'redux/actions/savedSearchActions'

export interface SavedSearchState {
  savedSearch: ISavedSearchFE | undefined
  savedRecentSearches: ISavedSearchFE[] | undefined
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
}

const initialState: SavedSearchState = {
  savedSearch: undefined,
  savedRecentSearches: undefined,
  status: 'idle',
  error: null,
}

const savedSearchSlice = createSlice({
  name: 'savedSearchList',
  initialState,
  reducers: {
    clearSavedSearch: (state) => {
      state.savedSearch = undefined
    },
    updateRecentSearches: (state, action) => {
      state.savedRecentSearches = action.payload
    },
    saveSavedSearchAs: (state, action) => {
      state.savedSearch = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSavedSearch.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchSavedSearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.savedSearch = action.payload
      })
      .addCase(fetchSavedSearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(updateSavedSearch.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(updateSavedSearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.savedSearch = action.payload
      })
      .addCase(updateSavedSearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(classifyTextWRecentSearch.pending, (state) => {
        state.status = 'loading'
      })
      //When we call classifyText it is either updating or adding a new search, which means
      //our recent searches has changed, so this thunkhook action returns all saved searches
      //we need to update the recent searches in this slice and also the saved search
      .addCase(classifyTextWRecentSearch.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.savedRecentSearches = action.payload?.recentSearches ?? []
        state.savedSearch = action.payload?.savedSearch
      })
      .addCase(classifyTextWRecentSearch.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(fetchSavedRecentSearches.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchSavedRecentSearches.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.savedRecentSearches = action.payload
      })
      .addCase(fetchSavedRecentSearches.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(deleteSavedRecentSearches.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(deleteSavedRecentSearches.fulfilled, (state, action) => {
        state.status = 'succeeded'
        //id of deleted search is returned so it gets filtered out of the recent searches
        state.savedRecentSearches = state.savedRecentSearches?.filter((f) => f.id != action.payload)
      })
      .addCase(deleteSavedRecentSearches.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
  },
})

export const {clearSavedSearch, updateRecentSearches, saveSavedSearchAs} = savedSearchSlice.actions
export default savedSearchSlice.reducer
